/**
 * TDS (Tax Deducted at Source) utility functions for Accounts & Billing
 * 
 * TDS is a mechanism where tax is deducted at the source of income.
 * When enabled for an account, the hotelier deducts TDS from payments,
 * so they pay a lower "net" amount while Stayflexi claims the TDS credit.
 */

/**
 * Calculate TDS bifurcation for a given gross amount and TDS percentage
 * @param {number} grossAmount - The full invoice/debit amount
 * @param {number} tdsPercentage - TDS percentage (e.g., 2, 5, 10)
 * @returns {Object} - { grossAmount, tdsAmount, netPayable }
 */
export const calculateTdsBifurcation = (grossAmount, tdsPercentage) => {
  const gross = parseFloat(grossAmount) || 0;
  const tdsPercent = parseFloat(tdsPercentage) || 0;
  
  const tdsAmount = (gross * tdsPercent) / 100;
  const netPayable = gross - tdsAmount;
  
  return {
    grossAmount: Math.round(gross * 100) / 100,
    tdsAmount: Math.round(tdsAmount * 100) / 100,
    tdsPercentage: tdsPercent,
    netPayable: Math.round(netPayable * 100) / 100,
  };
};

/**
 * Check if an account has TDS enabled
 * @param {Object} accountDetails - Account details object
 * @returns {boolean}
 */
export const isTdsEnabled = (accountDetails) => {
  const tdsPercent = parseFloat(accountDetails?.tds_percentage);
  return Number.isFinite(tdsPercent) && tdsPercent > 0;
};

/**
 * Get TDS percentage from account details
 * @param {Object} accountDetails - Account details object
 * @returns {number} - TDS percentage or 0 if not configured
 */
export const getTdsPercentage = (accountDetails) => {
  const tdsPercent = parseFloat(accountDetails?.tds_percentage);
  return Number.isFinite(tdsPercent) && tdsPercent >= 0 ? tdsPercent : 0;
};

/**
 * Format currency amount for display
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (INR, USD, etc.)
 * @returns {string} - Formatted amount string
 */
export const formatCurrency = (amount, currency = "INR") => {
  const num = parseFloat(amount) || 0;
  const symbol = currency === "INR" ? "₹" : "$";
  return `${symbol}${num.toFixed(2)}`;
};

/**
 * Check if a ledger entry is eligible for Pay Now
 * Pay Now is available for unpaid DEBIT entries
 * @param {Object} entry - Ledger entry
 * @returns {boolean}
 */
export const isEligibleForPayNow = (entry) => {
  if (!entry) return false;
  
  const isDebit = entry?.type?.toUpperCase() === "DEBIT";
  const isUnpaid = entry?.payment_status !== "PAID" && entry?.status !== "PAID";
  const hasPositiveAmount = parseFloat(entry?.amount) > 0;
  
  return isDebit && isUnpaid && hasPositiveAmount;
};

/**
 * Get the net payable amount for a ledger entry considering TDS
 * @param {Object} entry - Ledger entry
 * @param {number} tdsPercentage - TDS percentage for the account
 * @returns {number} - Net payable amount
 */
export const getNetPayableAmount = (entry, tdsPercentage) => {
  const grossAmount = parseFloat(entry?.amount) || 0;
  const { netPayable } = calculateTdsBifurcation(grossAmount, tdsPercentage);
  return netPayable;
};

/**
 * Create Razorpay order options for a debit entry payment
 * @param {Object} params - Payment parameters
 * @returns {Object} - Razorpay options object
 */
export const createRazorpayOrderOptions = ({
  amount,
  currency = "INR",
  accountName,
  accountEmail,
  groupId,
  ledgerId,
  razorpayKey,
  onSuccess,
  onFailure,
}) => {
  const amountInPaise = Math.round(amount * 100);
  
  return {
    key: razorpayKey,
    amount: amountInPaise.toString(),
    currency: currency,
    name: "Stayflexi",
    description: `Payment for Invoice #${ledgerId}`,
    image: "https://stayflexi.com/logo.png",
    handler: onSuccess,
    prefill: {
      name: accountName || "",
      email: accountEmail || "",
      contact: "",
    },
    notes: {
      account_id: groupId,
      ledger_id: ledgerId,
      module_source: "ACCOUNT_INVOICE_PAYMENT",
    },
    theme: {
      color: "#1ab394",
      hide_topbar: false,
    },
    modal: {
      ondismiss: onFailure,
    },
  };
};

/**
 * Validate TDS percentage input
 * @param {string|number} value - Input value
 * @returns {Object} - { isValid, error, value }
 */
export const validateTdsPercentage = (value) => {
  const numValue = parseFloat(value);
  
  if (value === "" || value === null || value === undefined) {
    return { isValid: true, error: null, value: 0 };
  }
  
  if (isNaN(numValue)) {
    return { isValid: false, error: "Please enter a valid number", value: 0 };
  }
  
  if (numValue < 0) {
    return { isValid: false, error: "TDS percentage cannot be negative", value: 0 };
  }
  
  if (numValue > 100) {
    return { isValid: false, error: "TDS percentage cannot exceed 100%", value: 100 };
  }
  
  return { isValid: true, error: null, value: numValue };
};

export default {
  calculateTdsBifurcation,
  isTdsEnabled,
  getTdsPercentage,
  formatCurrency,
  isEligibleForPayNow,
  getNetPayableAmount,
  createRazorpayOrderOptions,
  validateTdsPercentage,
};
